import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { getApiBaseUrl, getBackendBaseUrl } from '../utils/backendBaseUrl';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { currentUser, emailVerified } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  // Mantieni l'ultimo abbonamento noto per evitare downgrade fasulli su errori di rete
  const lastKnownSubscriptionRef = React.useRef(null);

  const BACKEND_URL = getBackendBaseUrl();
  const API = getApiBaseUrl();
  const isProd =
    BACKEND_URL.startsWith('https://sostegnopro.it') ||
    BACKEND_URL.startsWith('https://www.sostegnopro.it');
  const SUBSCRIPTION_TIMEOUT = isProd ? 25000 : 8000;

  const fetchSubscription = useCallback(async () => {
    if (!currentUser) {
      setSubscription(null);
      lastKnownSubscriptionRef.current = null;
      setLoading(false);
      return;
    }

    // Se l'email non è verificata, l'utente non può accedere al backend.
    if (emailVerified === false) {
      setSubscription({
        plan: 'free',
        plan_name: 'Free',
        status: 'trial',
        max_students: 1,
        max_classes: 1,
        ai_credits: { remaining: 10, monthly_limit: 10, extra: 0, total_available: 10 },
        pending_plan: null
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = await currentUser.getIdToken();
      const response = await axios.get(`${API}/payments/subscription`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: SUBSCRIPTION_TIMEOUT
      });
      const data = response.data || null;
      lastKnownSubscriptionRef.current = data;
      setSubscription(data);
    } catch (error) {
      console.error('Errore caricamento abbonamento:', error);
      // Se abbiamo un abbonamento precedente (es. piano premium), lo usiamo come fallback
      // per evitare che un errore di rete faccia sembrare l'utente sul piano free.
      if (lastKnownSubscriptionRef.current) {
        setSubscription(lastKnownSubscriptionRef.current);
      } else {
        // Prima volta e rete non disponibile: fallback conservativo
        setSubscription({
          plan: 'free',
          plan_name: 'Free',
          status: 'trial',
          max_students: 1,
          max_classes: 1,
          ai_credits: { remaining: 10, monthly_limit: 10, extra: 0, total_available: 10 },
          pending_plan: null
        });
      }
    } finally {
      setLoading(false);
    }
  }, [API, SUBSCRIPTION_TIMEOUT, currentUser, emailVerified]);

  // Fetch subscription on mount and when user changes
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Function to refresh subscription data (call after plan change or credit usage)
  const refreshSubscription = useCallback(async () => {
    await fetchSubscription();
  }, [fetchSubscription]);

  // Function to update credits locally (optimistic update)
  const decrementCredits = useCallback((amount = 1) => {
    setSubscription(prev => {
      if (!prev) return prev;
      const ai = prev.ai_credits || {};

      const unlimited = ai.total_available === 'illimitati' || ai.remaining === 'illimitati';
      if (unlimited) return prev;

      const planRemaining = typeof ai.remaining === 'number' ? ai.remaining : 0;
      const extraRemaining = typeof ai.extra === 'number' ? ai.extra : 0;
      const totalAvailable = typeof ai.total_available === 'number' ? ai.total_available : (planRemaining + extraRemaining);

      const usedFromPlan = Math.min(planRemaining, amount);
      const usedFromExtra = Math.max(0, amount - usedFromPlan);

      const newPlan = Math.max(0, planRemaining - usedFromPlan);
      const newExtra = Math.max(0, extraRemaining - usedFromExtra);
      const newTotal = Math.max(0, totalAvailable - amount);

      return {
        ...prev,
        ai_credits: {
          ...ai,
          remaining: newPlan,
          extra: newExtra,
          total_available: newTotal
        }
      };
    });
  }, []);

  const value = {
    subscription,
    loading,
    refreshSubscription,
    decrementCredits,
    // Helper getters
    currentPlan: subscription?.plan || 'free',
    planName: subscription?.plan_name || 'Free',
    // Crediti utilizzabili: include crediti extra
    creditsRemaining: subscription?.ai_credits?.total_available ?? subscription?.ai_credits?.remaining ?? 0,
    creditsLimit: subscription?.ai_credits?.monthly_limit ?? 0,
    hasUnlimitedCredits:
      subscription?.ai_credits?.total_available === 'illimitati' ||
      subscription?.ai_credits?.remaining === 'illimitati',
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
